import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Leaf, PiggyBank, BadgeEuro, Clock, TrendingUp, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";

// Base annual-saving share of current energy spend, per intervention
const SAVINGS = { heatpump: 0.4, biomass: 0.3, solar: 0.25, pv: 0.35, hybrid: 0.45 };
// Conto Termico 3.0 indicative incentive share of eligible cost
const INCENTIVE = { heatpump: 0.55, biomass: 0.5, solar: 0.5, pv: 0.4, hybrid: 0.6 };
// Indicative maximum eligible incentive (private baseline), in EUR
const INCENTIVE_CAP = { heatpump: 5000, biomass: 5000, solar: 3500, pv: 6000, hybrid: 8000 };
// map simulator choices -> configurator prefill
const SERVICE_SLUG = { heatpump: "termici", biomass: "termici", solar: "fotovoltaico", pv: "fotovoltaico", hybrid: "termici" };
const BEN_BUILDING = { private: "home", business: "business", pa: "public" };
const SOLAR = { solar: true, pv: true };

const fmtEur = (n, lang) =>
    new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n));
const fmtNum = (n, lang) => new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-GB", { maximumFractionDigits: 0 }).format(Math.round(n));

export default function Simulatore({ dark = false }) {
    const { t, lang } = useLang();
    const s = t.simulator;
    const navigate = useNavigate();

    const [intervention, setIntervention] = useState("heatpump");
    const [beneficiary, setBeneficiary] = useState("private");
    const [zone, setZone] = useState("centro");
    const [spend, setSpend] = useState(2500);
    const [cost, setCost] = useState(14000);

    const res = useMemo(() => {
        // Climate factor: heating interventions save more in the North (colder),
        // solar interventions produce more in the South (higher irradiance).
        const climateFactor = SOLAR[intervention]
            ? { nord: 0.85, centro: 1.0, sud: 1.2 }[zone]
            : { nord: 1.15, centro: 1.0, sud: 0.82 }[zone];

        const savePct = Math.min(0.7, SAVINGS[intervention] * climateFactor);
        const annualSaving = spend * savePct;

        // Incentive: percentage of eligible cost, capped, with a boost for public/business
        let incPct = INCENTIVE[intervention];
        let cap = INCENTIVE_CAP[intervention];
        if (beneficiary === "pa") { incPct = Math.min(0.65, incPct + 0.05); cap *= 2; }
        else if (beneficiary === "business") { cap *= 1.5; }
        const incentive = Math.min(cost * incPct, cap);
        const effPct = cost > 0 ? incentive / cost : incPct;

        const kwh = annualSaving / 0.28;
        const co2 = kwh * 0.23; // kg/year
        const netCost = Math.max(0, cost - incentive);
        const payback = annualSaving > 0 ? netCost / annualSaving : 0;
        const tenYear = annualSaving * 10;
        return { annualSaving, incentive, effPct, co2, payback, netCost, tenYear };
    }, [intervention, beneficiary, zone, spend, cost]);

    const label = dark ? "text-slate-300" : "text-slate-500";
    const cardBg = dark ? "bg-white/5 border-white/10" : "bg-white border-slate-200";
    const pill = (activeSel) =>
        `px-4 py-2.5 text-sm font-medium border transition-colors ${
            activeSel
                ? "bg-cb-accent border-cb-accent text-white"
                : dark
                ? "border-white/20 text-white/80 hover:border-cb-accent"
                : "border-slate-300 text-slate-600 hover:border-cb-accent hover:text-cb-accent"
        }`;
    const heading = dark ? "text-white" : "text-cb-dark";
    const numInput = `w-28 text-right bg-transparent border-b ${dark ? "border-white/20 text-white" : "border-slate-300 text-cb-dark"} text-cb-accent font-semibold focus:outline-none focus:border-cb-accent px-1 py-0.5`;

    const stats = [
        { icon: PiggyBank, label: s.saving, value: fmtEur(res.annualSaving, lang), suffix: s.perYear, accent: true },
        { icon: BadgeEuro, label: s.incentive, value: fmtEur(res.incentive, lang), suffix: `· ${Math.round(res.effPct * 100)}%` },
        { icon: Leaf, label: s.co2, value: `${fmtNum(res.co2, lang)} kg`, suffix: s.perYear },
        { icon: Clock, label: s.payback, value: res.payback ? res.payback.toFixed(1) : "—", suffix: s.years },
        { icon: TrendingUp, label: s.tenYear, value: fmtEur(res.tenYear, lang), suffix: "", span: true },
    ];

    const goToConfigurator = () => {
        const note =
            `${s.interventions[intervention]} — ${s.zone}: ${s.zones[zone]}. ${s.annualSpend}: ${fmtEur(spend, lang)}, ${s.interventionCost}: ${fmtEur(cost, lang)}. ` +
            `${s.saving}: ${fmtEur(res.annualSaving, lang)}${s.perYear}, ${s.incentive}: ${fmtEur(res.incentive, lang)} (${Math.round(res.effPct * 100)}%).`;
        const params = new URLSearchParams({
            tab: "config",
            service: SERVICE_SLUG[intervention],
            building: BEN_BUILDING[beneficiary],
            note,
        });
        navigate(`/preventivo?${params.toString()}`);
    };

    const clamp = (v, min, max) => Math.min(max, Math.max(min, isNaN(v) ? min : v));

    return (
        <div data-testid="simulatore" className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Inputs */}
            <div className="space-y-7">
                <div>
                    <div className={`text-xs uppercase tracking-[0.2em] mb-3 ${label}`}>{s.intervention}</div>
                    <div className="flex flex-wrap gap-2">
                        {Object.keys(s.interventions).map((k) => (
                            <button key={k} data-testid={`sim-int-${k}`} onClick={() => setIntervention(k)} className={pill(intervention === k)}>
                                {s.interventions[k]}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <div className={`text-xs uppercase tracking-[0.2em] mb-3 ${label}`}>{s.beneficiary}</div>
                    <div className="flex flex-wrap gap-2">
                        {Object.keys(s.beneficiaries).map((k) => (
                            <button key={k} data-testid={`sim-ben-${k}`} onClick={() => setBeneficiary(k)} className={pill(beneficiary === k)}>
                                {s.beneficiaries[k]}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <div className={`text-xs uppercase tracking-[0.2em] mb-3 ${label}`}>{s.zone}</div>
                    <div className="flex flex-wrap gap-2">
                        {Object.keys(s.zones).map((k) => (
                            <button key={k} data-testid={`sim-zone-${k}`} onClick={() => setZone(k)} className={pill(zone === k)}>
                                {s.zones[k]}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <div className={`flex justify-between items-center text-xs uppercase tracking-[0.2em] mb-3 ${label}`}>
                        <span>{s.annualSpend}</span>
                        <input data-testid="sim-spend-input" type="number" min="500" max="20000" step="100" value={spend}
                            onChange={(e) => setSpend(clamp(Number(e.target.value), 500, 20000))}
                            className={numInput} />
                    </div>
                    <input data-testid="sim-spend" type="range" min="500" max="20000" step="100" value={spend}
                        onChange={(e) => setSpend(Number(e.target.value))}
                        className="w-full accent-cb-accent cursor-pointer" />
                </div>

                <div>
                    <div className={`flex justify-between items-center text-xs uppercase tracking-[0.2em] mb-3 ${label}`}>
                        <span>{s.interventionCost}</span>
                        <input data-testid="sim-cost-input" type="number" min="2000" max="120000" step="500" value={cost}
                            onChange={(e) => setCost(clamp(Number(e.target.value), 2000, 120000))}
                            className={numInput} />
                    </div>
                    <input data-testid="sim-cost" type="range" min="2000" max="120000" step="500" value={cost}
                        onChange={(e) => setCost(Number(e.target.value))}
                        className="w-full accent-cb-accent cursor-pointer" />
                </div>
            </div>

            {/* Results */}
            <div>
                <div className={`text-xs uppercase tracking-[0.2em] mb-4 ${label}`}>{s.resultsTitle}</div>
                <div className="grid grid-cols-2 gap-3">
                    {stats.map((st, i) => {
                        const Icon = st.icon;
                        return (
                            <div key={i} data-testid={`sim-result-${i}`} className={`border p-5 ${st.span ? "col-span-2" : ""} ${st.accent ? "bg-cb-accent border-cb-accent text-white" : cardBg}`}>
                                <div className={`flex items-center gap-2 ${st.span ? "justify-between" : ""}`}>
                                    <Icon size={22} strokeWidth={1.5} className={st.accent ? "text-white" : "text-cb-accent"} />
                                    {st.span && <span className={`text-xs ${label}`}>{s.netCost}: {fmtEur(res.netCost, lang)}</span>}
                                </div>
                                <motion.div key={st.value} initial={{ opacity: 0.4, y: 4 }} animate={{ opacity: 1, y: 0 }}
                                    className={`mt-4 font-display text-2xl md:text-3xl font-bold ${st.accent ? "text-white" : heading}`}>
                                    {st.value}
                                </motion.div>
                                <div className={`text-xs mt-1 ${st.accent ? "text-white/80" : label}`}>{st.label} {st.suffix}</div>
                            </div>
                        );
                    })}
                </div>
                <p className={`mt-4 text-xs leading-relaxed ${label}`}>{s.disclaimer}</p>
                <button data-testid="sim-cta" onClick={goToConfigurator}
                    className="cb-glow mt-5 inline-flex items-center gap-2 bg-cb-dark hover:bg-cb-slate text-white px-7 py-3.5 font-medium transition-colors">
                    {s.cta} <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
}
